import React, { useEffect, useState, useCallback } from 'react';
import { Search, X, ChevronRight, Building2, RefreshCw, Mail, MapPin, ExternalLink, Edit2, Trash2, Plus, Wallet } from 'lucide-react';
import api from '../../services/api';
import { toast } from '../../components/ui/sonner';
import ClientFormDialog from './ClientFormDialog';
import ClientAccount from './ClientAccount';

const STATUS_BADGE = {
  active:   'bg-emerald-50 text-emerald-700 border-emerald-200',
  inactive: 'bg-slate-100 text-slate-500 border-slate-200',
  pending:  'bg-amber-50 text-amber-700 border-amber-200',
  on_hold:  'bg-rose-50 text-rose-700 border-rose-200',
};

export default function ClientsList() {
  const [clients, setClients]   = useState([]);
  const [total, setTotal]       = useState(0);
  const [source, setSource]     = useState('');
  const [db, setDb]             = useState('');
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [statusFilter, setStatus] = useState('');
  const [selected, setSelected] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError]       = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [formInitial, setFormInitial] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    const p = new URLSearchParams();
    if (search)       p.set('search', search);
    if (statusFilter) p.set('status', statusFilter);
    api.get(`/api/clients${p.toString() ? '?' + p : ''}`)
      .then(r => {
        setClients(r.data.clients || []);
        setTotal(r.data.total || 0);
        setSource(r.data.source || '');
        setDb(r.data.db || '');
      })
      .catch(e => {
        const msg = e?.response?.data?.detail || 'Failed to load clients from Pulse';
        setError(msg);
        setClients([]);
        setTotal(0);
      })
      .finally(() => setLoading(false));
  }, [search, statusFilter]);

  useEffect(() => { load(); }, [load]);

  // Customers are held in FinOps now, not read through a Pulse cache, so there
  // is nothing to invalidate -- this just re-reads the list.
  const refresh = async () => {
    setRefreshing(true);
    try {
      await load();
      toast.success('Customer list refreshed');
    } catch {
      toast.error('Failed to refresh customers');
    } finally {
      setRefreshing(false);
    }
  };

  const openCreate = () => { setFormInitial(null);    setFormOpen(true); };
  const openEdit   = () => { setFormInitial(selected); setFormOpen(true); };

  const handleSaved = (saved) => {
    // Replace or insert in-place so the list stays in sync without a full reload
    setClients(prev => {
      const exists = prev.some(c => c.client_id === saved.client_id);
      return exists
        ? prev.map(c => c.client_id === saved.client_id ? saved : c)
        : [...prev, saved];
    });
    setTotal(t => selected && selected.client_id === saved.client_id ? t : t + (clients.some(c => c.client_id === saved.client_id) ? 0 : 1));
    setSelected(saved);
  };

  const handleDelete = async () => {
    if (!selected) return;
    if (!window.confirm(`Delete "${selected.legal_name || selected.name}" from Pulse?  This cannot be undone.`)) return;
    setDeleting(true);
    try {
      await api.delete(`/api/clients/${selected.client_id}`);
      setClients(prev => prev.filter(c => c.client_id !== selected.client_id));
      setTotal(t => Math.max(0, t - 1));
      setSelected(null);
      toast.success('Client deleted from Pulse');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to delete client');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="flex h-[calc(100vh-60px)] overflow-hidden fade-in" data-testid="clients-page">

      {/* Left pane — list */}
      <div className="w-[420px] min-w-[320px] flex flex-col border-r border-slate-200 bg-white">
        <div className="px-4 pt-5 pb-3 border-b border-slate-200">
          <div className="flex items-center justify-between mb-1">
            <h1 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Clients</h1>
            <span className="text-xs text-slate-400" data-testid="clients-total">{total.toLocaleString()} total</span>
          </div>
          <div className="flex items-center gap-1.5 mb-3">
            <p className="text-[11px] text-slate-400 flex-1">Customer master · {source || 'finops'}</p>
            <button onClick={openCreate}
              className="inline-flex items-center gap-1 px-2 py-0.5 bg-indigo-900 text-white text-[10px] font-semibold rounded hover:bg-indigo-800"
              data-testid="clients-new-button">
              <Plus size={11}/> New
            </button>
            <button onClick={refresh} disabled={refreshing}
              className="text-[10px] text-indigo-700 hover:text-indigo-900 inline-flex items-center gap-1 disabled:opacity-50"
              data-testid="clients-refresh">
              <RefreshCw size={10} className={refreshing ? 'animate-spin' : ''} /> Refresh
            </button>
          </div>
          <div className="relative mb-3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="clients-search"
              placeholder="Search legal name, code, email…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="w-full pl-8 pr-3 py-1.5 border border-slate-200 rounded-md text-sm focus:outline-none focus:border-indigo-400"
            />
            {search && <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400"><X size={12}/></button>}
          </div>
          <div className="flex gap-1" data-testid="client-status-tabs">
            {['', 'active', 'inactive', 'pending', 'on_hold'].map(s => (
              <button key={s || 'all'} onClick={() => setStatus(s)}
                data-testid={`status-tab-${s || 'all'}`}
                className={`px-2.5 py-1 text-xs font-medium rounded-full transition-colors ${statusFilter === s ? 'bg-indigo-900 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
                {s ? s.replace('_', ' ') : 'All'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto divide-y divide-slate-100">
          {loading ? (
            [...Array(6)].map((_, i) => (
              <div key={i} className="px-4 py-3 flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-slate-100 animate-pulse" />
                <div className="flex-1"><div className="h-3 bg-slate-100 rounded animate-pulse mb-1.5 w-3/4"/><div className="h-2.5 bg-slate-100 rounded animate-pulse w-1/2"/></div>
              </div>
            ))
          ) : error ? (
            <div className="px-4 py-12 text-center text-rose-500 text-sm" data-testid="clients-error">
              {error}
            </div>
          ) : clients.length === 0 ? (
            <div className="px-4 py-12 text-center text-slate-400 text-sm">
              {search || statusFilter ? 'No clients match your filters' : 'No clients found in Pulse'}
            </div>
          ) : (
            clients.map(c => {
              const isActive = selected?.client_id === c.client_id;
              return (
                <button key={c.client_id} onClick={() => setSelected(c)}
                  data-testid={`client-row-${c.client_id}`}
                  className={`w-full text-left px-4 py-3 flex items-center gap-3 transition-colors hover:bg-slate-50 ${isActive ? 'bg-indigo-50 border-r-2 border-indigo-700' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${isActive ? 'bg-indigo-700 text-white' : 'bg-slate-100 text-slate-600'}`}>
                    <Building2 size={14} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-slate-900 truncate">{c.legal_name || c.trading_name || c.client_code}</p>
                    <p className="text-xs text-slate-400 truncate">{c.client_code} · {c.billing_email || c.country || '—'}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded-full border ${STATUS_BADGE[c.status] || STATUS_BADGE.inactive}`}>
                      {c.status || 'unknown'}
                    </span>
                    <ChevronRight size={12} className="text-slate-300" />
                  </div>
                </button>
              );
            })
          )}
        </div>
      </div>

      {/* Right pane — detail */}
      {!selected ? (
        <div className="flex-1 flex items-center justify-center text-slate-400">
          <div className="text-center max-w-md px-6">
            <Building2 size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm mb-1">Select a client to view details</p>
            <p className="text-xs text-slate-300">Customers are held in FinOps. A record that came from Eyvo Pulse keeps its Pulse id, but Pulse is not required to create, edit or read one.</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 overflow-y-auto bg-slate-50" data-testid="client-detail-panel">
          <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between sticky top-0 z-10">
            <div>
              <h2 className="text-lg font-bold text-slate-900 flex items-center gap-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
                {selected.legal_name || selected.trading_name || selected.client_code}
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full border ${STATUS_BADGE[selected.status] || STATUS_BADGE.inactive}`}>
                  {selected.status || 'unknown'}
                </span>
              </h2>
              <p className="text-xs text-slate-400 mt-0.5">{selected.client_code} · {selected.client_id}</p>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={openEdit}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 text-slate-700 text-xs font-medium rounded-md hover:bg-slate-100"
                data-testid="edit-client-button">
                <Edit2 size={13}/> Edit
              </button>
              <button onClick={handleDelete} disabled={deleting}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-rose-200 text-rose-600 text-xs font-medium rounded-md hover:bg-rose-50 disabled:opacity-50"
                data-testid="delete-client-button">
                <Trash2 size={13}/> {deleting ? 'Deleting…' : 'Delete'}
              </button>
              <button onClick={() => setSelected(null)} className="text-slate-400 hover:text-slate-700"><X size={16}/></button>
            </div>
          </div>

          <div className="px-6 py-5 space-y-6">
            {/* What this customer owes, before what their address is. */}
            <Section title="Account" icon={Wallet}>
              <ClientAccount clientId={selected.client_id} currency={selected.currency_code} />
            </Section>

            <Section title="Identity" icon={Building2}>
              <div className="grid grid-cols-2 gap-4">
                <ReadField label="Legal Name"           value={selected.legal_name} />
                <ReadField label="Trading Name"         value={selected.trading_name} />
                <ReadField label="Client Code"          value={selected.client_code} />
                <ReadField label="Billing Contact"      value={selected.billing_contact_name} />
                <ReadField label="Currency"             value={selected.currency_code} />
                <ReadField label="Timezone"             value={selected.timezone} />
              </div>
            </Section>

            <Section title="Contact" icon={Mail}>
              <div className="grid grid-cols-2 gap-4">
                <ReadField label="Billing Email"        value={selected.billing_email} />
                <ReadField label="Phone"                value={selected.phone} />
              </div>
            </Section>

            <Section title="Address" icon={MapPin}>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><ReadField label="Address Line 1" value={selected.address_line_1} /></div>
                <div className="col-span-2"><ReadField label="Address Line 2" value={selected.address_line_2} /></div>
                <ReadField label="City"         value={selected.city} />
                <ReadField label="State/Region" value={selected.state_region} />
                <ReadField label="Postal Code"  value={selected.postal_code} />
                <ReadField label="Country"      value={selected.country} />
              </div>
            </Section>

            <Section title="Billing Terms" icon={ExternalLink}>
              <div className="grid grid-cols-2 gap-4">
                <ReadField label="Payment Terms (days)"      value={selected.payment_terms_days} />
                <ReadField label="Reminder Days Before Due"  value={selected.default_reminder_days_before_due} />
                <ReadField label="Do Not Chase"              value={selected.do_not_chase === true ? 'Yes' : selected.do_not_chase === false ? 'No' : null} />
                <ReadField label="System Start"              value={selected.system_start_date} />
                <ReadField label="System Expiration"         value={selected.system_expiration_date} />
              </div>
            </Section>

            {selected.notes && (
              <Section title="Notes" icon={Mail}>
                <p className="text-sm text-slate-700 whitespace-pre-wrap">{selected.notes}</p>
              </Section>
            )}
          </div>
        </div>
      )}

      <ClientFormDialog
        open={formOpen}
        client={formInitial}
        onClose={() => setFormOpen(false)}
        onSaved={handleSaved}
      />
    </div>
  );
}

function ReadField({ label, value }) {
  return (
    <div>
      <label className="block text-xs text-slate-400 mb-0.5">{label}</label>
      <p className="text-sm text-slate-800 min-h-[1.25rem]">{value || value === 0 ? String(value) : <span className="text-slate-300">—</span>}</p>
    </div>
  );
}

function Section({ title, icon: Icon, children }) {
  return (
    <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
      <div className="px-4 py-2.5 border-b border-slate-100 bg-slate-50 flex items-center gap-2">
        <Icon size={13} className="text-slate-500" />
        <span className="text-xs font-semibold text-slate-600 uppercase tracking-wide">{title}</span>
      </div>
      <div className="p-4">{children}</div>
    </div>
  );
}
